<template>
  <section class="checklist-anexos" :aria-labelledby="`titulo-anexos-${idCheckListItem}`">
    <span :id="`titulo-anexos-${idCheckListItem}`" class="sr-only">Anexos do item</span>
    <b-button
      v-if="exibeAcionador"
      size="sm"
      variant="outline-primary"
      :disabled="!habilitado"
      :title="habilitado ? 'Gerenciar anexos do item' : 'Marque o item como problema para habilitar os anexos'"
      @click="abrirGerenciador"
    >
      <b-icon icon="paperclip" />
      <span class="d-none d-md-inline ml-1">Anexos</span>
      <b-badge v-if="anexos.length" variant="primary" class="ml-1">{{ anexos.length }}</b-badge>
    </b-button>

    <b-modal
      v-model="gerenciadorAberto"
      size="xl"
      hide-footer
      centered
      title-class="titulo-modal-anexo"
      :title="nomeItem ? `Anexos — ${nomeItem}` : `Anexos do item ${idCheckListItem}`"
      @shown="carregarAnexosPendentes"
      @hidden="limparSelecao"
    >

    <div v-if="permiteEnvio" class="mb-4">
      <b-form-file
        ref="seletor"
        accept="image/*"
        multiple
        browse-text="Selecionar"
        placeholder="Selecione uma ou mais imagens"
        drop-placeholder="Solte as imagens aqui"
        :disabled="enviando"
        @change="selecionarArquivos"
      />
      <b-alert v-if="erroSelecao" show variant="danger" class="mt-2 mb-0">{{ erroSelecao }}</b-alert>

      <div v-if="arquivosSelecionados.length" class="mt-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <strong>{{ arquivosSelecionados.length }} {{ arquivosSelecionados.length === 1 ? 'imagem selecionada' : 'imagens selecionadas' }}</strong>
          <span class="text-muted small">{{ formatarTamanho(tamanhoTotalSelecionado) }}</span>
        </div>
        <div class="preview-grid">
          <div v-for="arquivo in arquivosSelecionados" :key="arquivo.id" class="preview-card">
            <img :src="arquivo.url" :alt="`Prévia de ${arquivo.file.name}`" class="preview-image">
            <div class="preview-info">
              <span class="nome-arquivo" :title="arquivo.file.name">{{ arquivo.file.name }}</span>
              <small class="text-muted">{{ formatarTamanho(arquivo.file.size) }}</small>
            </div>
            <b-button size="sm" variant="outline-danger" :disabled="enviando" :aria-label="`Remover ${arquivo.file.name} da seleção`" title="Remover da seleção" @click="removerSelecionado(arquivo.id)">
              <b-icon icon="x" />
            </b-button>
          </div>
        </div>
        <b-progress v-if="enviando" :value="progresso" :max="100" show-progress animated class="mt-3" />
        <b-button variant="primary" class="mt-3" :disabled="enviando" @click="enviar">
          <b-spinner v-if="enviando" small class="mr-1" />
          {{ enviando ? `Enviando (${progresso}%)` : 'Salvar imagens' }}
        </b-button>
      </div>
    </div>

    <b-alert v-if="erroOperacao" show variant="danger">{{ erroOperacao }}</b-alert>

    <div v-if="anexosOrdenados.length" class="galeria-grid">
      <article v-for="anexo in anexosOrdenados" :key="anexo.idCheckListAnexo" class="anexo-card">
        <button type="button" class="miniatura-button" :disabled="!urlsAnexos[anexo.idCheckListAnexo]" :aria-label="`Abrir ${anexo.nomeArquivo}`" @click="abrirImagem(anexo)">
          <img v-if="urlsAnexos[anexo.idCheckListAnexo]" :src="urlsAnexos[anexo.idCheckListAnexo]" :alt="anexo.nomeArquivo" class="anexo-image">
          <div v-else class="anexo-placeholder">
            <b-spinner v-if="carregandoIds[anexo.idCheckListAnexo]" small />
            <template v-else><b-icon icon="image" font-scale="1.8" /><small>Imagem indisponível</small></template>
          </div>
        </button>
        <div class="anexo-info">
          <strong class="nome-arquivo" :title="anexo.nomeArquivo">{{ anexo.nomeArquivo }}</strong>
          <small class="text-muted">{{ formatarTamanho(anexo.tamanho) }} · {{ formatarData(anexo.dataCriacao) }}</small>
          <small v-if="errosAnexos[anexo.idCheckListAnexo]" class="text-danger">{{ errosAnexos[anexo.idCheckListAnexo] }}</small>
        </div>
        <div class="anexo-acoes">
          <b-button size="sm" variant="outline-primary" :disabled="!urlsAnexos[anexo.idCheckListAnexo]" title="Abrir imagem" @click="abrirImagem(anexo)"><b-icon icon="arrows-fullscreen" /></b-button>
          <b-button v-if="errosAnexos[anexo.idCheckListAnexo]" size="sm" variant="outline-secondary" title="Tentar carregar novamente" @click="carregarImagem(anexo)"><b-icon icon="arrow-clockwise" /></b-button>
          <b-button
            v-if="exibeExclusao"
            size="sm"
            variant="outline-danger"
            :disabled="!permiteExclusao || excluindoIds[anexo.idCheckListAnexo]"
            :title="permiteExclusao ? 'Excluir imagem' : 'Não é permitido excluir anexos de um checklist finalizado'"
            @click="confirmarExclusao(anexo)"
          >
            <b-spinner v-if="excluindoIds[anexo.idCheckListAnexo]" small /><b-icon v-else icon="trash" />
          </b-button>
        </div>
      </article>
    </div>
    <p v-else class="text-muted mb-0">Nenhuma imagem anexada.</p>

    </b-modal>

    <b-modal
      v-model="imagemAberta"
      size="xl"
      hide-footer
      centered
      title-class="titulo-modal-anexo"
      :title="anexoAberto ? anexoAberto.nomeArquivo : 'Imagem'"
    >
      <div class="imagem-completa-container">
        <img v-if="anexoAberto" :src="urlsAnexos[anexoAberto.idCheckListAnexo]" :alt="anexoAberto.nomeArquivo" class="imagem-completa">
      </div>
    </b-modal>
  </section>
</template>

<script>
import CheckListAnexoService from '../services/CheckListAnexoService';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_REQUEST_SIZE = 50 * 1024 * 1024;

export default {
  name: 'CheckListAnexos',
  props: {
    idCheckListItem: { type: Number, required: true },
    nomeItem: { type: String, default: '' },
    anexos: { type: Array, default: () => [] },
    habilitado: { type: Boolean, default: false },
    permiteEnvio: { type: Boolean, default: false },
    permiteExclusao: { type: Boolean, default: true },
    exibeExclusao: { type: Boolean, default: true },
    exibeAcionador: { type: Boolean, default: true }
  },
  data() {
    return {
      arquivosSelecionados: [], urlsAnexos: {}, carregandoIds: {}, errosAnexos: {}, excluindoIds: {},
      erroSelecao: '', erroOperacao: '', enviando: false, progresso: 0, gerenciadorAberto: false, imagemAberta: false,
      anexoAberto: null, proximoIdLocal: 1, destruido: false
    };
  },
  computed: {
    anexosOrdenados() { return [...this.anexos].sort((a, b) => new Date(b.dataCriacao) - new Date(a.dataCriacao)); },
    tamanhoTotalSelecionado() { return this.arquivosSelecionados.reduce((total, item) => total + item.file.size, 0); }
  },
  watch: {
    habilitado(valor) {
      if (!valor) this.gerenciadorAberto = false;
    },
    anexos: {
      immediate: true,
      handler(novosAnexos) {
        const idsAtuais = new Set(novosAnexos.map(anexo => String(anexo.idCheckListAnexo)));
        Object.keys(this.urlsAnexos).forEach(id => { if (!idsAtuais.has(id)) this.liberarUrlAnexo(id); });
        if (this.gerenciadorAberto) this.carregarAnexosPendentes();
      }
    }
  },
  beforeDestroy() {
    this.destruido = true;
    this.limparSelecao();
    Object.keys(this.urlsAnexos).forEach(this.liberarUrlAnexo);
  },
  methods: {
    abrirGerenciador() {
      if (!this.habilitado) return;
      this.gerenciadorAberto = true;
    },
    carregarAnexosPendentes() {
      this.anexos.forEach(anexo => {
        if (!this.urlsAnexos[anexo.idCheckListAnexo] && !this.carregandoIds[anexo.idCheckListAnexo]) this.carregarImagem(anexo);
      });
    },
    validarImagens(files) {
      if (!files.length) return 'Selecione pelo menos uma imagem.';
      if (files.some(file => !file.type || !file.type.startsWith('image/'))) return 'Selecione somente arquivos de imagem.';
      if (files.some(file => file.size === 0)) return 'Não é permitido enviar imagens vazias.';
      if (files.some(file => file.size > MAX_FILE_SIZE)) return 'Cada imagem deve ter no máximo 10 MB.';
      if (files.reduce((total, file) => total + file.size, 0) > MAX_REQUEST_SIZE) return 'O envio deve ter no máximo 50 MB.';
      return null;
    },
    selecionarArquivos(event) {
      const novos = Array.from(event.target.files || []);
      if (!novos.length) return;
      const todos = this.arquivosSelecionados.map(item => item.file).concat(novos);
      const erro = this.validarImagens(todos);
      this.erroSelecao = erro || '';
      event.target.value = '';
      if (erro) return;
      novos.forEach(file => this.arquivosSelecionados.push({ id: this.proximoIdLocal++, file, url: URL.createObjectURL(file) }));
    },
    removerSelecionado(id) {
      const indice = this.arquivosSelecionados.findIndex(item => item.id === id);
      if (indice < 0) return;
      URL.revokeObjectURL(this.arquivosSelecionados[indice].url);
      this.arquivosSelecionados.splice(indice, 1);
      this.erroSelecao = '';
    },
    limparSelecao() {
      this.arquivosSelecionados.forEach(item => URL.revokeObjectURL(item.url));
      this.arquivosSelecionados = [];
      if (this.$refs.seletor) this.$refs.seletor.reset();
    },
    async enviar() {
      if (this.enviando) return;
      const files = this.arquivosSelecionados.map(item => item.file);
      const erro = this.validarImagens(files);
      if (erro) { this.erroSelecao = erro; return; }
      this.enviando = true; this.progresso = 0; this.erroOperacao = '';
      try {
        const response = await CheckListAnexoService.enviar(this.idCheckListItem, files, event => {
          if (event.total) this.progresso = Math.round((event.loaded * 100) / event.total);
        });
        this.progresso = 100;
        this.$emit('anexos-adicionados', Array.isArray(response.data) ? response.data : []);
        this.limparSelecao();
        this.$bvToast.toast('Imagens enviadas com sucesso.', { title: 'Sucesso', variant: 'success', solid: true });
      } catch (error) { this.erroOperacao = await this.obterMensagemErro(error); }
      finally { this.enviando = false; }
    },
    async carregarImagem(anexo) {
      const id = anexo.idCheckListAnexo;
      this.$set(this.carregandoIds, id, true); this.$delete(this.errosAnexos, id);
      try {
        const response = await CheckListAnexoService.carregar(this.idCheckListItem, id);
        const url = URL.createObjectURL(response.data);
        if (this.destruido || !this.anexos.some(item => item.idCheckListAnexo === id)) URL.revokeObjectURL(url);
        else { this.liberarUrlAnexo(id); this.$set(this.urlsAnexos, id, url); }
      } catch (error) { this.$set(this.errosAnexos, id, await this.obterMensagemErro(error, 'Não foi possível carregar a imagem.')); }
      finally { this.$delete(this.carregandoIds, id); }
    },
    liberarUrlAnexo(id) {
      if (this.urlsAnexos[id]) URL.revokeObjectURL(this.urlsAnexos[id]);
      this.$delete(this.urlsAnexos, id);
    },
    abrirImagem(anexo) {
      if (!this.urlsAnexos[anexo.idCheckListAnexo]) return;
      this.anexoAberto = anexo; this.imagemAberta = true;
    },
    confirmarExclusao(anexo) {
      if (!this.permiteExclusao) return;
      this.$bvModal.msgBoxConfirm(`Deseja excluir a imagem “${anexo.nomeArquivo}”?`, {
        title: 'Excluir imagem', okTitle: 'Excluir', cancelTitle: 'Cancelar', okVariant: 'danger', centered: true
      }).then(confirmado => { if (confirmado) this.excluir(anexo); });
    },
    async excluir(anexo) {
      if (!this.permiteExclusao) return;
      const id = anexo.idCheckListAnexo;
      this.$set(this.excluindoIds, id, true); this.erroOperacao = '';
      try {
        await CheckListAnexoService.excluir(this.idCheckListItem, id);
        if (this.anexoAberto && this.anexoAberto.idCheckListAnexo === id) { this.imagemAberta = false; this.anexoAberto = null; }
        this.liberarUrlAnexo(id); this.$emit('anexo-excluido', id);
        this.$bvToast.toast('Imagem excluída com sucesso.', { title: 'Sucesso', variant: 'success', solid: true });
      } catch (error) { this.erroOperacao = await this.obterMensagemErro(error); }
      finally { this.$delete(this.excluindoIds, id); }
    },
    async obterMensagemErro(error, fallback = 'Não foi possível processar os anexos.') {
      const status = error && error.response && error.response.status;
      let data = error && error.response && error.response.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try { data = await data.text(); } catch (e) { data = ''; }
      }
      if (typeof data === 'string' && data.trim()) return data;
      if (data && typeof data.message === 'string' && data.message.trim()) return data.message;
      if (status === 413) return 'O envio excedeu o limite máximo de 50 MB.';
      if (status === 404) return 'Item, anexo ou arquivo não encontrado.';
      if (status === 500) return 'Ocorreu um erro no servidor. Tente novamente.';
      return fallback;
    },
    formatarTamanho(bytes) {
      if (!Number.isFinite(Number(bytes))) return '-';
      if (Number(bytes) < 1024) return `${bytes} B`;
      if (Number(bytes) < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatarData(data) {
      if (!data) return '-';
      const valor = new Date(data);
      return Number.isNaN(valor.getTime()) ? '-' : valor.toLocaleString('pt-BR');
    }
  }
};
</script>

<style scoped>
/* Grid moderno */
.preview-grid, .galeria-grid { 
  display: grid; 
  gap: 1rem; 
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); 
}

/* Estilo premium para os cards */
.preview-card, .anexo-card { 
  display: flex; 
  align-items: center; 
  gap: 1rem; 
  padding: 1rem; 
  border: none; 
  border-radius: 12px; 
  background: #ffffff; 
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.preview-card:hover, .anexo-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(48, 63, 159, 0.15);
}

.anexo-card { 
  align-items: stretch; 
  flex-direction: column; 
}

/* Miniaturas de imagem */
.preview-image { 
  width: 64px; 
  height: 64px; 
  object-fit: cover; 
  border-radius: 8px; 
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.miniatura-button { 
  width: 100%; 
  height: 180px; 
  padding: 0; 
  overflow: hidden; 
  border: none; 
  border-radius: 8px; 
  background: #f4f6f9; 
  cursor: pointer; 
  transition: opacity 0.2s ease;
}

.miniatura-button:hover:not(:disabled) {
  opacity: 0.9;
}

.anexo-image { 
  width: 100%; 
  height: 100%; 
  object-fit: cover; 
  transition: transform 0.4s ease;
}

.miniatura-button:hover .anexo-image {
  transform: scale(1.05);
}

.anexo-placeholder { 
  display: flex; 
  height: 100%; 
  align-items: center; 
  justify-content: center; 
  flex-direction: column; 
  gap: .5rem; 
  color: #6c757d; 
}

.anexo-acoes { 
  display: flex; 
  gap: .5rem; 
  justify-content: flex-end; 
}

/* Informações da imagem */
.preview-info, .anexo-info { 
  display: flex; 
  flex: 1; 
  flex-direction: column; 
  min-width: 0; 
}

.nome-arquivo { 
  font-weight: 600;
  color: #2c3e50;
  overflow: hidden; 
  text-overflow: ellipsis; 
  white-space: nowrap; 
}

/* Modal e Visualização da imagem ampliada */
::v-deep .titulo-modal-anexo {
  font-weight: 700;
  color: #303f9f;
  min-width: 0;
  max-width: calc(100% - 2.5rem);
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
}

.imagem-completa-container { 
  display: flex; 
  justify-content: center; 
  max-height: 80vh; 
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1rem;
}

.imagem-completa { 
  max-width: 100%; 
  max-height: 75vh; 
  object-fit: contain; 
  border-radius: 8px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
}

/* Estilização moderna do File Input (Dropzone) seguro */
::v-deep .custom-file {
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

::v-deep .custom-file:hover {
  box-shadow: 0 6px 16px rgba(48, 63, 159, 0.15);
}

::v-deep .custom-file-label {
  border-radius: 8px;
  background: #fafbfe;
  color: #495057;
  font-weight: 500;
  border: 1px solid #c4cce0;
  cursor: pointer;
}

::v-deep .custom-file-input:hover ~ .custom-file-label {
  border-color: #303f9f;
  background: #f0f3fa;
}

::v-deep .custom-file-label::after {
  background: #303f9f;
  color: white;
  border-radius: 0 8px 8px 0;
  font-weight: 600;
}

@media (max-width: 575.98px) {
  ::v-deep .titulo-modal-anexo { font-size: 1rem; line-height: 1.3; }
}
</style>
